import React, { useEffect, useRef } from 'react';
import { NostrEvent, NPool, NRelay1 } from '@nostrify/nostrify';
import { NostrContext } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/hooks/useAppContext';

interface NostrProviderProps {
  children: React.ReactNode;
  userReadRelays?: string[];
  userWriteRelays?: string[];
}

const NostrProvider: React.FC<NostrProviderProps> = (props) => {
  const { children, userReadRelays = [], userWriteRelays = [] } = props;
  const { config, presetRelays } = useAppContext();

  const queryClient = useQueryClient();

  // Create NPool instance only once
  const pool = useRef<NPool | undefined>(undefined);

  // Use refs so the pool always has the latest data
  const relayUrl = useRef<string>(config.relayUrl);
  const userReadRelaysRef = useRef<string[]>(userReadRelays);
  const userWriteRelaysRef = useRef<string[]>(userWriteRelays);
  const defaultRelayUrls = useRef<string[]>([]);

  // Update refs when config or user relays change
  useEffect(() => {
    relayUrl.current = config.relayUrl;
    userReadRelaysRef.current = userReadRelays;
    userWriteRelaysRef.current = userWriteRelays;

    // Store preset relay URLs for default connections
    defaultRelayUrls.current = (presetRelays ?? []).map(r => r.url);

    // Invalidate queries when relay configuration changes
    queryClient.invalidateQueries({ queryKey: ['nostr'] });
  }, [config.relayUrl, userReadRelays, userWriteRelays, presetRelays, queryClient]);

  // Initialize NPool only once
  if (!pool.current) {
    pool.current = new NPool({
      open(url: string) {
        return new NRelay1(url);
      },
      reqRouter(filters) {
        const relays = new Set<string>();

        // Always include the selected relay for compatibility
        relays.add(relayUrl.current);

        // If user is logged in and has relays, use their read relays
        if (userReadRelaysRef.current.length > 0) {
          for (const url of userReadRelaysRef.current) {
            relays.add(url);
            // Limit to prevent overwhelming number of connections
            if (relays.size >= 10) break;
          }
        } else {
          // Otherwise, use default preset relays
          for (const url of defaultRelayUrls.current) {
            relays.add(url);
            // Connect to all preset relays (typically 4-5)
            if (relays.size >= 6) break;
          }
        }

        // Create a map with the same filters for all relays
        const relayMap = new Map<string, typeof filters>();
        for (const url of relays) {
          relayMap.set(url, filters);
        }

        return relayMap;
      },
      eventRouter(_event: NostrEvent) {
        const relays = new Set<string>();

        // If user is logged in and has write relays, use those
        if (userWriteRelaysRef.current.length > 0) {
          for (const url of userWriteRelaysRef.current) {
            relays.add(url);
            // Limit write relays to prevent excessive publishing
            if (relays.size >= 8) break;
          }
        } else {
          // Otherwise, publish to selected relay + preset relays
          relays.add(relayUrl.current);

          for (const url of defaultRelayUrls.current) {
            relays.add(url);
            if (relays.size >= 5) break;
          }
        }

        return [...relays];
      },
    });
  }

  return (
    <NostrContext.Provider value={{ nostr: pool.current }}>
      {children}
    </NostrContext.Provider>
  );
};

export default NostrProvider;